import { useMemo } from 'react';

import type { SortDirection } from '@klareospace/pharos-lib-types';

import type { DataTableSorting } from '../components/composed/data-table/data-table';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UseDataTableSortingOptions<TColumn extends string> {
  onSort: (field: TColumn, direction: SortDirection | null) => void;
  sortBy?: TColumn;
  sortDirection?: SortDirection;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Wraps the sort-adapter pattern duplicated in every sortable table.
 * Converts a typed column-specific onSort handler into the generic
 * DataTableSorting shape expected by <DataTable />.
 */
export function useDataTableSorting<TColumn extends string>({
  onSort,
  sortBy,
  sortDirection,
}: UseDataTableSortingOptions<TColumn>): DataTableSorting {
  return useMemo<DataTableSorting>(
    () => ({
      onSort: (field: string, direction): void => onSort(field as TColumn, direction),
      sortBy,
      sortDirection,
    }),
    [onSort, sortBy, sortDirection],
  );
}
